import api from "../api";
import React, { useEffect, useState, useContext } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  CalendarMonth,
  Edit,
  Place,
  Business,
} from "@mui/icons-material";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useSalonData } from "./salonDataContext";
import { DeadlineContext } from "../App"

const isEmpty = (value) => value === null || value === undefined || value === "";

// Valeur affichée, avec un tiret discret quand le champ n'est pas renseigné.
function Value({ children }) {
  if (isEmpty(children)) {
    return <Typography component="span" color="text.disabled">—</Typography>;
  }
  return children;
}

// Carte de section : titre, action optionnelle à droite, contenu.
function Section({ title, action, children }) {
  return (
    <Paper variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3, py: 2, minHeight: 64 }}
      >
        <Typography variant="h6">{title}</Typography>
        {action}
      </Stack>
      <Divider />
      <Box sx={{ p: 3, flexGrow: 1 }}>{children}</Box>
    </Paper>
  );
}

// Paire libellé / valeur empilée, pour les informations générales.
function Field({ label, value }) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={500}>
        <Value>{value}</Value>
      </Typography>
    </Box>
  );
}

// Note sur 10 avec une jauge.
function Score({ label, value }) {
  const hasValue = !isEmpty(value);
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" fontWeight={600}>
          {hasValue ? `${value} / 10` : <Value />}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={hasValue ? Number(value) * 10 : 0}
        sx={{ height: 8, borderRadius: 4 }}
      />
    </Box>
  );
}

// Bloc de texte libre, qui passe à la ligne au lieu d'être tronqué.
function TextBlock({ label, value }) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        <Value>{value}</Value>
      </Typography>
    </Box>
  );
}

const FicheSalon = () => {
  const { id: salonId } = useParams();
  const navigate = useNavigate();
  const [salon, setSalon] = useState(null);
  const { setSalonData } = useSalonData();
  const deadline = new Date(useContext(DeadlineContext));
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');

  useEffect(() => {
    const fetchSalon = async () => {
      setLoading(true);
      try {
        const response = await api.get("/events/getFicheSalon", {
          params: {
            salonId: salonId,
          },
        });
        if (response.data.success) {
          setSalon(response.data.data[0]);
          setSalonData(response.data.data[0]);
          setStartDate(new Date(response.data.data[0].StartDate));
        } else {
          console.error("Failed to fetch salon details:", response.data.error);
        }
      } catch (err) {
        console.error("An error occurred:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSalon();
  }, [salonId, setSalonData]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!salon) {
    return (
      <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 4 }, py: 4 }}>
        <Alert severity="error">Impossible de charger ce salon.</Alert>
      </Box>
    );
  }

  const formatDate = (date) => (date ? new Date(date).toLocaleDateString() : null);
  const sameDay = salon.StartDate === salon.EndDate;

  // Entonnoir : prospects → candidats potentiels → candidats → inscrits.
  // "Candidats" n'existe que pour l'année en cours.
  const funnel = [
    { label: "Prospects rencontrés", n: salon.ProspectsMetN__c, n1: salon.ProspectsMetN1__c, n2: salon.ProspectsMetN2__c },
    { label: "Candidats potentiels", n: salon.PotentielCandidateN__c, n1: salon.PotentialCandidateN1__c, n2: salon.PotentialCandidateN2__c },
    { label: "Candidats", n: salon.DCN__c },
    { label: "Inscrits", n: salon.RegisteredN__c, n1: salon.RegisteredN1__c, n2: salon.RegisteredN2__c },
  ];

  const scores = [
    { label: "Qualité de l'emplacement", value: salon.LocationQuality__c },
    { label: "Qualité des prospects", value: salon.LeadQuality__c },
    { label: "Qualité du trafic", value: salon.TrafficQuality__c },
    { label: "Qualité de l'organisation", value: salon.QualityOfTheReception__c },
  ];

  const currentDate = new Date()

  let updateMessage = '';
  let updateSeverity = 'info';
  if (currentDate < startDate) {
    updateMessage = `Les données des salons ne sont pas encore modifiables. Vous pourrez les modifier à partir du ${startDate.toLocaleDateString()}.`;
  } else if (currentDate < deadline) {
    updateMessage = `Vous pouvez modifier les données de ce salon jusqu'au ${deadline.toLocaleDateString()}.`;
  } else if (currentDate.toDateString() === new Date(deadline).toDateString()) {
    updateMessage = "C'est le dernier jour pour modifier les données des salons.";
    updateSeverity = 'warning';
  } else if (currentDate > deadline) {
    updateMessage = `Les données des salons ne sont plus modifiables depuis le ${deadline.toLocaleDateString()}.`;
    updateSeverity = 'warning';
  }

  const canEdit = currentDate <= deadline && currentDate >= startDate;

  // Le formulaire de modification ouvre la section mémorisée dans localStorage.
  const editButton = (section) =>
    canEdit && (
      <Button
        component={Link}
        to={`/UpdateFicheSalon/${salonId}`}
        onClick={() => localStorage.setItem('section', section)}
        variant="contained"
        size="small"
        startIcon={<Edit />}
      >
        Modifier
      </Button>
    );

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 4 }, py: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate("/SalonsList")}
        sx={{ mb: 2, ml: -1 }}
      >
        Vos salons
      </Button>

      <Typography variant="h4" component="h1" gutterBottom>
        {salon.Name}
      </Typography>

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 3 }}>
        <Chip
          icon={<CalendarMonth />}
          label={sameDay ? formatDate(salon.StartDate) : `${formatDate(salon.StartDate)} → ${formatDate(salon.EndDate)}`}
          variant="outlined"
        />
        {salon.City__c && <Chip icon={<Place />} label={salon.City__c} variant="outlined" />}
        {salon.Organizer__r?.Name && <Chip icon={<Business />} label={salon.Organizer__r.Name} variant="outlined" />}
      </Stack>

      {updateMessage && (
        <Alert severity={updateSeverity} sx={{ mb: 3 }}>
          {updateMessage}
        </Alert>
      )}

      <Stack spacing={3}>
        <Section title="Informations">
          <Grid container spacing={3}>
            <Grid item xs={6} md={2}><Field label="Date de début" value={formatDate(salon.StartDate)} /></Grid>
            <Grid item xs={6} md={2}><Field label="Date de fin" value={formatDate(salon.EndDate)} /></Grid>
            <Grid item xs={6} md={2}><Field label="Ville" value={salon.City__c} /></Grid>
            <Grid item xs={6} md={2}><Field label="Organisateur" value={salon.Organizer__r?.Name} /></Grid>
            <Grid item xs={6} md={2}>
              <Field label="Superficie louée" value={isEmpty(salon.RentedArea__c) ? null : `${salon.RentedArea__c} m²`} />
            </Grid>
            <Grid item xs={6} md={2}><Field label="N° de stand" value={salon.StandNumber__c} /></Grid>
          </Grid>
        </Section>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Section title="Bilan quantitatif" action={editButton('quantitatif')}>
              <Table size="small" sx={{ "& td, & th": { px: 1 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell align="right" sx={{ fontWeight: 600 }}>N</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: "text.secondary" }}>N-1</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: "text.secondary" }}>N-2</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {funnel.map((row) => (
                    <TableRow key={row.label} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                      <TableCell component="th" scope="row">{row.label}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: "1rem" }}>
                        <Value>{row.n}</Value>
                      </TableCell>
                      <TableCell align="right" sx={{ color: "text.secondary" }}>
                        {"n1" in row ? <Value>{row.n1}</Value> : ""}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "text.secondary" }}>
                        {"n2" in row ? <Value>{row.n2}</Value> : ""}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Section>
          </Grid>

          <Grid item xs={12} md={6}>
            <Section title="Bilan qualitatif" action={editButton('qualitatif')}>
              <Stack spacing={2.5}>
                {scores.map((s) => <Score key={s.label} {...s} />)}
              </Stack>
            </Section>
          </Grid>
        </Grid>

        <Section title="Retours et suite à donner" action={editButton('qualitatif')}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}><TextBlock label="Observations" value={salon.Observations__c} /></Grid>
            <Grid item xs={12} md={6}><TextBlock label="Axes d'amélioration" value={salon.Improvments__c} /></Grid>
            <Grid item xs={12} md={4}><Field label="À refaire l'an prochain" value={salon.ToRedo__c} /></Grid>
            {salon.ToRedo__c === "Oui" && (
              <>
                <Grid item xs={12} md={4}><Field label="Superficie souhaitée" value={salon.DesiredArea__c} /></Grid>
                <Grid item xs={12} md={4}><Field label="Emplacement souhaité" value={salon.DesiredLocation__c} /></Grid>
              </>
            )}
          </Grid>
        </Section>
      </Stack>
    </Box>
  );
};

export default FicheSalon;
